import { RequestLog } from '@engine/models'

import { ActivityTimeline } from '#components/ActivityTimeline'
import { EmptyState } from '#components/EmptyState'
import { ErrorsSection } from '#components/ErrorsSection'
import { KeyValueDisplay } from '#components/KeyValueDisplay'
import { RequestLogSections } from '#components/RequestLogSections'
import { formatDateTime } from '#utils/format'

import { DocumentLookupStatusBadge } from './DocumentLookupStatusBadge'

interface DocumentLookupDetailsProps {
  document: RequestLog | null
}

const DocumentLookupDetails = ({ document }: DocumentLookupDetailsProps) => {
  if (!document) {
    return <EmptyState title="Select a document lookup to view details" />
  }

  const requestContent = document.tenantRequest?.content as {
    externalId?: string
    trackingNumber?: string
  }
  const responseContent = document.tenantResponse?.content as {
    accountId?: string
    carrierCode?: string
    documents?: unknown[]
    error?: string
    errors?: unknown[]
    errorType?: string
    externalId?: string
    trackingNumber?: string
  }
  const errorType = responseContent?.errorType
  const errors =
    responseContent?.errors ||
    (responseContent?.error ? [responseContent.error] : undefined)
  const externalId =
    document.externalId ||
    requestContent?.externalId ||
    responseContent?.externalId ||
    requestContent?.trackingNumber ||
    responseContent?.trackingNumber
  const carrier =
    responseContent?.carrierCode ||
    document.carrierCode?.[0] ||
    (document.serviceRequest?.[0]?.content as { code?: string } | undefined)
      ?.code

  return (
    <div className="space-y-6">
      <KeyValueDisplay
        items={[
          { label: 'External ID', value: externalId },
          {
            label: 'PRO Number',
            value:
              requestContent?.trackingNumber || responseContent?.trackingNumber,
          },
          { label: 'Request ID', value: document.requestId },
          { label: 'Tenant ID', value: document.tenantId },
          { label: 'Type', value: document.type },
          { label: 'Carrier', value: carrier || 'N/A' },
          { label: 'Account ID', value: responseContent?.accountId },
          {
            label: 'Documents',
            value: responseContent?.documents?.length,
          },
          {
            label: 'Request Date',
            value: formatDateTime(document.tenantRequest?.requestDate),
          },
          {
            label: 'Status',
            value: <DocumentLookupStatusBadge log={document} />,
          },
          ...(errorType ? [{ label: 'Error Type', value: errorType }] : []),
        ]}
      />

      <ErrorsSection
        errors={errors}
        errorType={errorType}
        carrier={carrier}
        requestId={document.requestId}
        externalId={externalId}
        service="documents"
      />

      <RequestLogSections requestLog={document} />

      <ActivityTimeline
        externalId={externalId}
        requestLogType="document"
        emptyMessage="No traces or request logs found for this document lookup."
      />
    </div>
  )
}

export { DocumentLookupDetails }
